/*
 * custom: scroll-header (v2, real-header architecture).
 *
 * Two states, on <body>, mobile collection pages only:
 * - custom-header-stuck: the shopper is past the grid start. The real
 *   header wrapper fixes to the top of the viewport, condensed, and
 *   the Filters/Sort bar slides up from the bottom edge. Both stay put
 *   until the shopper scrolls back above the grid — no direction
 *   detection, no expand/collapse.
 * - custom-grid-ended: the end of the grid is on screen. The bar slides
 *   back out (the header stays); there is nothing left to filter
 *   through, and it would only cover the last row and the footer.
 *
 * The header section keeps its natural height as a reserved slot
 * while the wrapper is fixed, so sticking causes zero layout shift.
 *
 * Everything interactive is the genuine header element — drawer,
 * predictive search, cart — so nothing is proxied or mirrored. The
 * one intervention kept from v1 is the pre-paint scroll pin around
 * drawer open/close: this store's body scroll-lock resets the scroll
 * position (body is display:grid), and the pin corrects it inside
 * the scroll event, before paint.
 */
(() => {
  'use strict';

  const CONFIG = {
    config: '[data-custom-scroll-header-config]',
    tools: '[data-custom-stuck-tools]',
    toolsFilter: '[data-stuck-tools-filter]',
    toolsTop: '[data-stuck-tools-top]',
    toolsSort: '[data-stuck-tools-sort]',
    themeSort: '#SortBy-mobile, #SortBy',
    container: '#ProductGridContainer',
    section: '.section-header',
    wrapper: '.section-header .header-wrapper',
    header: '.section-header .header',
    drawerSummary: '.section-header header-drawer > details > summary',
    facetsOpener: 'summary.mobile-facets__open-wrapper',
    stuckClass: 'custom-header-stuck',
    endedClass: 'custom-grid-ended',
    offsetVar: '--custom-scroll-header-offset',
    slotVar: '--custom-stuck-slot',
    mobileQuery: '(max-width: 989px)',
  };

  const configEl = document.querySelector(CONFIG.config);
  const container = document.querySelector(CONFIG.container);
  const section = document.querySelector(CONFIG.section);
  const wrapper = document.querySelector(CONFIG.wrapper);
  const header = document.querySelector(CONFIG.header);
  if (!configEl || !container || !section || !wrapper || !header) return;

  const body = document.body;
  let pinY = null;

  /* ---- condensed-title injection ----------------------------------- */

  // Plain text, not a control: tapping the collection name to return to
  // the grid start was undiscoverable — nothing about a header title
  // suggests it scrolls — and the bar's own "Back to top" covers the
  // intent anyway.
  const title = document.createElement('span');
  title.className = 'custom-stuck-title';
  const titleText = document.createElement('span');
  titleText.className = 'custom-stuck-title__text';
  titleText.textContent = configEl.dataset.collectionTitle;
  title.appendChild(titleText);
  header.appendChild(title);

  function scrollToPageTop() {
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  }

  /* ---- tools strip lives inside the fixed wrapper ------------------ */

  const tools = document.querySelector(CONFIG.tools);
  if (tools) {
    // Body, not the header wrapper: this is a bottom bar now, and it
    // must not inherit the header's fixed positioning or its stacking.
    document.body.appendChild(tools);
    // Mounted = always rendered, parked below the viewport edge, so
    // showing it is a transform transition rather than a display flip.
    tools.classList.add('custom-stuck-tools--mounted');
  }

  /* ---- stuck search icon next to the cart -------------------------- */

  const stuckSearch = document.querySelector('details-modal.custom-stuck-search');
  const cartAnchor = header.querySelector('#cart-icon-bubble');
  if (stuckSearch && cartAnchor && cartAnchor.parentElement) {
    cartAnchor.parentElement.insertBefore(stuckSearch, cartAnchor);
  }

  /* ---- reserved slot: no layout shift when the wrapper fixes ------- */

  function syncSlot() {
    if (body.classList.contains(CONFIG.stuckClass)) return; // only measure natural flow
    const h = Math.round(section.getBoundingClientRect().height);
    if (h > 0) document.documentElement.style.setProperty(CONFIG.slotVar, h + 'px');
  }
  syncSlot();
  addEventListener('resize', () => {
    if (!body.classList.contains(CONFIG.stuckClass)) syncSlot();
  }, { passive: true });

  /* ---- state ------------------------------------------------------- */

  function syncOffset() {
    const stuck = body.classList.contains(CONFIG.stuckClass) && matchMedia(CONFIG.mobileQuery).matches;
    const offset = stuck ? Math.round(wrapper.getBoundingClientRect().height) + 'px' : '0px';
    document.documentElement.style.setProperty(CONFIG.offsetVar, offset);
  }

  function setStuck(next) {
    if (body.classList.contains(CONFIG.stuckClass) === next) return;
    if (next) syncSlot();
    body.classList.toggle(CONFIG.stuckClass, next);
    syncOffset();
  }

  // Marker at the container top: past it = inside the grid.
  // display:block inline beats base.css's div:empty hiding.
  const marker = document.createElement('div');
  marker.style.cssText = 'display:block;position:absolute;top:0;height:1px;width:1px;pointer-events:none;';
  container.style.position = 'relative';
  container.prepend(marker);

  // ...and one at the container end. In flow, not absolute: it has to
  // sit after the last row so it tracks the grid's real bottom.
  const endMarker = document.createElement('div');
  endMarker.style.cssText = 'display:block;height:1px;width:1px;pointer-events:none;';
  container.append(endMarker);

  function update() {
    setStuck(marker.getBoundingClientRect().bottom < 0);
    // Once the end of the grid is on screen the bar has nothing left to
    // filter through, and it would sit over the last row and then the
    // footer — so it slides back out.
    body.classList.toggle(CONFIG.endedClass, endMarker.getBoundingClientRect().top < innerHeight);
  }

  new IntersectionObserver(() => update(), { rootMargin: '0px 0px 999999px 0px' }).observe(marker);

  let ticking = false;

  addEventListener(
    'scroll',
    () => {
      if (pinY !== null) {
        if (Math.abs(window.scrollY - pinY) > 1) window.scrollTo(0, pinY);
        return;
      }
      if (ticking) return;
      ticking = true;
      setTimeout(() => {
        update();
        ticking = false;
      }, 100);
    },
    { passive: true }
  );

  // Dawn replaces the grid markup on every facet re-render, taking the
  // markers with it.
  new MutationObserver(() => {
    if (!marker.isConnected) container.prepend(marker);
    if (!endMarker.isConnected) container.append(endMarker);
  }).observe(container, { childList: true });

  /* ---- scroll pin around drawer open/close ------------------------- */

  function endPin() {
    if (pinY === null) return;
    if (Math.abs(window.scrollY - pinY) > 1) window.scrollTo(0, pinY);
    pinY = null;
    body.classList.remove('custom-header-no-anim');
    update();
  }

  function pinAround(details) {
    pinY = window.scrollY;
    body.classList.add('custom-header-no-anim');
    if (!details) {
      setTimeout(endPin, 1000);
      return;
    }
    new MutationObserver((records, observer) => {
      if (!details.hasAttribute('open')) {
        // Dawn removes the scroll lock around the close animation.
        setTimeout(endPin, 350);
        observer.disconnect();
      }
    }).observe(details, { attributes: true, attributeFilter: ['open'] });
  }

  // The real hamburger: let Dawn handle the click, just pin the
  // scroll for the drawer's lifecycle — only needed once stuck
  // (unstuck, the shopper is at the top anyway; a pin is harmless).
  const drawerSummary = document.querySelector(CONFIG.drawerSummary);
  if (drawerSummary) {
    drawerSummary.addEventListener('click', () => {
      if (pinY === null) pinAround(drawerSummary.closest('details'));
    });
  }

  const toolsFilter = tools && tools.querySelector(CONFIG.toolsFilter);
  if (toolsFilter) {
    toolsFilter.addEventListener('click', () => {
      const opener = document.querySelector(CONFIG.facetsOpener);
      if (!opener) return;
      pinAround(opener.closest('details'));
      opener.click();
    });
  }

  // A facets re-render supersedes any pin: the shopper lands at the
  // start of the refreshed grid (custom-load-more scrolls there).
  document.addEventListener('custom:grid-rerendered', () => {
    pinY = null;
    body.classList.remove('custom-header-no-anim');
  });

  // The whole page, not the grid start: this is now the only way up.
  const topButton = tools && tools.querySelector(CONFIG.toolsTop);
  if (topButton) topButton.addEventListener('click', scrollToPageTop);

  /* ---- sort: native picker, Dawn does the re-render ---------------- */

  const sortSelect = tools && tools.querySelector(CONFIG.toolsSort);
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const themeSort = document.querySelector(CONFIG.themeSort);
      if (!themeSort) return;
      themeSort.value = sortSelect.value;
      // Dawn's facet form listens for `input` (debounced) and handles
      // the fetch, URL update, and grid swap from there.
      themeSort.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Dawn replaces the facets markup on every re-render, so re-read
    // the authoritative value instead of trusting our own.
    document.addEventListener('custom:grid-rerendered', () => {
      const themeSort = document.querySelector(CONFIG.themeSort);
      if (themeSort && themeSort.value) sortSelect.value = themeSort.value;
    });
  }

})();
